/**
 * TopNav Component
 *
 * Primary horizontal navigation bar showing section tabs.
 * Renders sections from navigationConfig with role-based visibility.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  Factory,
  Calendar,
  Clock,
  BarChart2,
  Settings,
  MapPin,
  ChevronDown,
  RefreshCw,
  Play,
  Pause,
} from 'lucide-react';
import { getVisibleSections } from '../../config/navigationConfig';
import AccountMenu from './AccountMenu';
import NotificationBell from '../NotificationBell';

const API_BASE = import.meta.env.VITE_API_URL || '';
const apiScopes = [
  'api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user',
];

// Work center storage key (matches WireframeApp)
const WORK_CENTER_STORAGE_KEY = '3dcolor-selected-workcenter';

// Available work centers
const WORK_CENTERS = [
  { id: 'PA', label: 'Production Art', color: 'bg-purple-100 text-purple-700' },
  { id: 'ASSEMBLY', label: 'Assembly', color: 'bg-blue-100 text-blue-700' },
  { id: 'SHIPPING', label: 'Shipping', color: 'bg-green-100 text-green-700' },
  { id: 'PAINT', label: 'Paint', color: 'bg-orange-100 text-orange-700' },
  { id: '3D MODEL', label: '3D Model', color: 'bg-pink-100 text-pink-700' },
  { id: 'PM', label: 'PM', color: 'bg-indigo-100 text-indigo-700' },
];

/**
 * Map icon names from config to Lucide components
 */
const ICON_MAP = {
  factory: Factory,
  calendar: Calendar,
  clock: Clock,
  'bar-chart-2': BarChart2,
  settings: Settings,
};

/**
 * Get icon component from name
 */
function getIcon(iconName) {
  return ICON_MAP[iconName] || null;
}

/**
 * Active Session Indicator Component
 * Shows a separate pill for each active session (operation + project)
 */
function ActiveSessionIndicator({ sessions }) {
  if (!sessions || sessions.length === 0) return null;

  // Only show running or paused sessions
  const activeSessions = sessions.filter(s => s.state === 'running' || s.state === 'paused');
  if (activeSessions.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      {activeSessions.map((session) => {
        const isRunning = session.state === 'running';
        const projectDisplay = session.quoteNumber || session.jobNumber || '';
        const clientCode = session.productCode || '';
        const operation = session.workCenter || '';

        return (
          <div
            key={session.ticketId || session.jobId}
            className={`
              inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
              ${isRunning
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-amber-100 text-amber-800 border border-amber-200'
              }
            `}
            title={`${operation} - ${projectDisplay} ${clientCode}`.trim()}
          >
            {isRunning ? (
              <Play className="w-3 h-3 fill-current" />
            ) : (
              <Pause className="w-3 h-3" />
            )}
            {operation && (
              <span className="font-medium">{operation}</span>
            )}
            <span className="opacity-75">
              {projectDisplay}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Work Center Selector Component
 */
function WorkCenterSelector({ currentWorkCenter, onSelect, canChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentWC = WORK_CENTERS.find((wc) => wc.id === currentWorkCenter);
  const displayLabel = currentWC?.label || currentWorkCenter || 'Not Set';
  const colorClass = currentWC?.color || 'bg-slate-100 text-gray-600';

  if (!canChange) {
    // Just show badge, no dropdown
    return (
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${colorClass}`}
        title="Your work center"
      >
        <MapPin className="w-3 h-3" />
        {displayLabel}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-colors hover:opacity-80 ${colorClass}`}
        title="Click to change work center"
      >
        <MapPin className="w-3 h-3" />
        {displayLabel}
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
            Select Work Center
          </div>
          {WORK_CENTERS.map((wc) => (
            <button
              key={wc.id}
              onClick={() => {
                onSelect(wc.id);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-200 flex items-center gap-2 ${
                wc.id === currentWorkCenter ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${wc.color.replace('text-', 'bg-').split(' ')[0]}`} />
              {wc.label}
              {wc.id === currentWorkCenter && (
                <span className="ml-auto text-xs text-purple-500">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * TopNav renders the primary navigation bar
 * @param {object} props
 * @param {string} props.activeSection - Currently active section ID
 * @param {object} props.user - User object
 * @param {function} props.onSectionClick - Handler for section clicks
 * @param {function} props.onAccountAction - Handler for account menu actions
 */
function TopNav({
  activeSection,
  user,
  onSectionClick,
  onAccountAction,
  onReprintRemake,
}) {
  const { instance, accounts } = useMsal();
  const visibleSections = getVisibleSections(user);

  // Active sessions state
  const [activeSessions, setActiveSessions] = useState([]);

  // Work center state from localStorage
  const [workCenter, setWorkCenter] = useState(() => {
    return localStorage.getItem(WORK_CENTER_STORAGE_KEY) || null;
  });

  // Auth helper
  const ensureToken = useCallback(async () => {
    if (accounts.length === 0) return null;
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        try {
          const tokenResponse = await instance.acquireTokenPopup({
            scopes: apiScopes,
            account: accounts[0],
          });
          return tokenResponse.accessToken;
        } catch (popupErr) {
          console.error('Token acquisition failed:', popupErr);
          return null;
        }
      }
      console.error('Token error:', err);
      return null;
    }
  }, [instance, accounts]);

  // Fetch active sessions for the current user
  const fetchActiveSessions = useCallback(async () => {
    try {
      const token = await ensureToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/me/time/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        // Convert sessions object to array
        const sessionsArray = data.sessions ? Object.values(data.sessions) : [];
        setActiveSessions(sessionsArray);
      }
    } catch (err) {
      console.error('Error fetching active sessions:', err);
    }
  }, [ensureToken]);

  // Fetch active sessions on mount and when time-updated event fires
  useEffect(() => {
    fetchActiveSessions();

    const handleTimeUpdated = () => {
      fetchActiveSessions();
    };

    window.addEventListener('time-updated', handleTimeUpdated);
    return () => window.removeEventListener('time-updated', handleTimeUpdated);
  }, [fetchActiveSessions]);

  // Listen for work center changes from AccountMenu
  useEffect(() => {
    const handleWorkCenterChanged = (event) => {
      setWorkCenter(event.detail);
    };
    window.addEventListener('workCenterChanged', handleWorkCenterChanged);
    return () => window.removeEventListener('workCenterChanged', handleWorkCenterChanged);
  }, []);

  // Determine if user can change work center (admins can always change)
  const canChangeWorkCenter = user?.isAdmin || false;

  // Handle work center change
  const handleWorkCenterChange = (newWorkCenter) => {
    setWorkCenter(newWorkCenter);
    if (newWorkCenter) {
      localStorage.setItem(WORK_CENTER_STORAGE_KEY, newWorkCenter);
    } else {
      localStorage.removeItem(WORK_CENTER_STORAGE_KEY);
    }
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('workCenterChanged', { detail: newWorkCenter }));
  };

  // Separate main sections from account dropdown
  const mainSections = visibleSections.filter((s) => s.renderAs !== 'dropdown');
  const accountSection = visibleSections.find((s) => s.id === 'account');

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Left: Logo + Section Tabs */}
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="flex-shrink-0">
              <span className="text-lg font-bold text-gray-900">
                3D<span className="text-purple-600">Color</span>
              </span>
            </div>

            {/* Section Tabs */}
            <div className="hidden md:flex items-center gap-1">
              {mainSections.map((section) => {
                const Icon = getIcon(section.icon);
                const isActive = activeSection === section.id;

                return (
                  <button
                    key={section.id}
                    onClick={() => onSectionClick(section.id)}
                    className={`
                      inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg
                      transition-colors
                      ${
                        isActive
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-slate-100'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Active Sessions + Rework + Work Center + Notification Bell + Account Menu */}
          <div className="flex items-center gap-3">
            {/* Active Session Indicator - Shows running sessions */}
            <ActiveSessionIndicator sessions={activeSessions} />

            {/* Reprint/Remake Button - Report issues needing reprints or remakes */}
            <button
              onClick={() => onReprintRemake?.()}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-amber-800 bg-gradient-to-r from-amber-200 to-yellow-200 hover:from-amber-300 hover:to-yellow-300 border border-amber-300 rounded-lg shadow-sm hover:shadow transition-all"
              title="Report a reprint or remake needed"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reprint/Remake</span>
            </button>

            {/* Work Center Indicator */}
            {workCenter && (
              <WorkCenterSelector
                currentWorkCenter={workCenter}
                onSelect={handleWorkCenterChange}
                canChange={canChangeWorkCenter}
              />
            )}

            {/* Notification Bell */}
            <NotificationBell
              userEmail={user?.email}
              onNavigate={(viewId) => {
                window.location.hash = `#/production/${viewId}`;
              }}
            />

            {/* Account Menu */}
            {accountSection && (
              <AccountMenu
                user={user}
                section={accountSection}
                onAction={onAccountAction}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile: Section Tabs (horizontal scroll) */}
      <div className="md:hidden border-t border-gray-100">
        <div className="flex overflow-x-auto px-4 py-2 gap-1 scrollbar-hide">
          {mainSections.map((section) => {
            const Icon = getIcon(section.icon);
            const isActive = activeSection === section.id;

            return (
              <button
                key={section.id}
                onClick={() => onSectionClick(section.id)}
                className={`
                  flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg
                  transition-colors
                  ${
                    isActive
                      ? 'bg-purple-100 text-purple-700'
                      : 'text-gray-600 hover:bg-slate-100'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                {Icon && <Icon className="w-4 h-4" />}
                {section.label}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

TopNav.propTypes = {
  activeSection: PropTypes.string,
  user: PropTypes.object,
  onSectionClick: PropTypes.func.isRequired,
  onAccountAction: PropTypes.func.isRequired,
  onReprintRemake: PropTypes.func,
};

TopNav.defaultProps = {
  activeSection: null,
  user: null,
};

export default TopNav;
